import { serverUrl } from '@utils/MkEnv'

export interface MkTokensDTO {
  googleMapsApiKey: string
  mkStorageSecret: string
  figmaApiToken: string
  aemSecurityApiKey: string
  dtApiToken: string
}

/**
 * Decodes a base64 encoded string
 */
function decodeBase64(encoded: string): string {
  if (!encoded) return ''
  try {
    return atob(encoded)
  } catch (error) {
    console.warn('Failed to decode base64 string:', error)
    return ''
  }
}

/**
 * Fetches tokens from the backend /tokens endpoint
 */
export async function fetchTokens(): Promise<MkTokensDTO | null> {
  try {
    // In Jest/jsdom (and some older Node versions), `fetch` is not available by default.
    // Treat missing fetch as "no tokens" rather than logging endlessly and keeping async work alive.
    if (typeof fetch !== 'function') {
      return null
    }
    const response = await fetch(`${serverUrl}/mkapi/v1/tokens`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      console.warn(`Failed to fetch tokens: ${response.status} ${response.statusText}`)
      return null
    }

    const tokens: MkTokensDTO = await response.json()
    return tokens
  } catch (error) {
    console.warn('Error fetching tokens:', error)
    return null
  }
}

/**
 * Fetches and decodes tokens from the backend
 */
export async function fetchDecodedTokens(): Promise<Partial<MkTokensDTO> | null> {
  try {
    const tokens = await fetchTokens()
    if (!tokens) return null

    return {
      googleMapsApiKey: decodeBase64(tokens.googleMapsApiKey),
      mkStorageSecret: decodeBase64(tokens.mkStorageSecret),
      figmaApiToken: decodeBase64(tokens.figmaApiToken),
      aemSecurityApiKey: decodeBase64(tokens.aemSecurityApiKey),
      dtApiToken: decodeBase64(tokens.dtApiToken),
    }
  } catch (error) {
    console.warn('Error fetching and decoding tokens:', error)
    return null
  }
}

/**
 * Utility function to refresh the token cache in configs.ts
 * This can be called when tokens need to be refreshed
 */
export async function refreshTokenCache(): Promise<void> {
  try {
    const tokens = await fetchDecodedTokens()
    if (tokens) {
      // Update the global token cache
      if (typeof window !== 'undefined' && (window as any).__tokenCache) {
        ;(window as any).__tokenCache = tokens
      }
    }
  } catch (error) {
    console.warn('Error refreshing token cache:', error)
  }
}
